<template>
    <div class="d-block mb-3 h-100" :key="post.id" :id="`post-${post.id}`"
         :class="{'post_needs_moderation': post.moderation_status === 'pending'}"
    >
        <div class="card-body h-100" style="display:flex; flex-direction:column; flex:1; position: relative;">
            <b-modal v-model="showDeleteConfirmModal"
                     title="Confirm Deletion"
                     ok-title="Yes"
                     cancel-title="No"
                     @ok="deletePost">
                <p class="my-2">Are you sure you want to delete this post?</p>
            </b-modal>

            <b-modal v-model="isChangelogDateModalVisible" title="Change date" hide-footer>
                <form @submit.prevent="submitChangeDate">
                    <label for="newdate-datepicker">New date</label>
                    <b-form-datepicker id="newdate-datepicker" v-model="new_date" class="mb-2"></b-form-datepicker>
                    <p class="mt-4 small">
                        Change the date of this changelog item. This will update:
                    </p>
                    <ul class=" small">
                        <li>Status change date</li>
                        <li>Post creation date (if it's newer)</li>
                        <li>Date for changelog comment</li>
                    </ul>
                    <p class="small">
                        See <a href="#" data-productlift-sidebar="6ddb7594-4c18-4c9d-b470-ff1ef4680979">ProductLift documentation</a> for more changelog edits.
                    </p>
                    <div class="d-flex justify-content-end">
<!--                        <b-button variant="outline" @click="closeModal">Cancel</b-button>-->
                        <b-button type="submit" variant="primary">Change</b-button>
                    </div>
                </form>
            </b-modal>

            <div class="row changelog-row">
                <p class="col-md-2 date-column">
                    {{post.latest_status_change_at_formatted}}
                    <b-button v-if="isAdmin" variant="link" @click="isChangelogDateModalVisible=true" class="date-edit-button">
                        <i class="bi bi-pencil-square text-body"></i>
                    </b-button>
                </p>

                <div class="col-md-10">
                    <div class="d-flex justify-content-between">
                        <a :href="post.url" class="post-link">
                            <h4 class="mb-3 p-0" style="word-wrap: break-word;">
                                {{ post.title }}
                                <span v-if="portalDisplayPostId" class="post_uid">{{ post.uid }}</span>
                            </h4>
                        </a>

                        <b-dropdown
                            v-if="isAdmin"
                            variant="outline"
                            size="sm"
                            no-caret
                            right
                            class="post-menu-dropdown"
                            toggle-class="card-menu-button"
                            :boundary="'viewport'"
                        >
                            <template v-slot:button-content>
                                <b-icon icon="gear" aria-hidden="true"></b-icon>
                            </template>
                            <b-dropdown-item @click.stop="goToPost">Open post</b-dropdown-item>
                            <b-dropdown-item @click.stop="editPost">Edit post</b-dropdown-item>
                            <b-dropdown-item @click.stop="isChangelogDateModalVisible=true">Change date</b-dropdown-item>
                            <b-dropdown-item @click.stop="showDeleteConfirmModal = true">Delete</b-dropdown-item>
                        </b-dropdown>
                    </div>

                    <div class="d-flex justify-content-between align-items-end w-100">
                        <div class="flex-grow-1 pr-2" style="word-wrap: break-word; min-width: 0; flex: 1;">
                            <status-badge v-if="options.display_status" :status="post.status" size="sm" class="mt-2" />

                            <category-badge v-if="options.display_categories" class="mt-2" v-for="category in post.categories" :key="category.id" :category="category" />

                            <span v-if="post.version" class="badge badge-sm badge-light text-muted text-nowrap mt-2">
                                <i class="fa fa-tag"></i> {{ post.version }}
                            </span>

                            <span v-if="post.release" class="badge badge-sm badge-light text-muted text-nowrap mt-2">
                                <i class="fa fa-cube"></i> {{ post.release }}
                            </span>

                            <span v-if="post.platform" class="badge badge-sm badge-light text-muted text-nowrap mt-2">
                                <i class="fa fa-desktop"></i> {{ post.platform }}
                            </span>

                            <span v-if="options.display_number_comments && post.comments_count > 0" class="badge badge-sm badge-light text-muted text-nowrap mt-2">
                                <i class="fa fa-commenting-o"></i> {{ post.comments_count }}
                            </span>
                        </div>
                    </div>

                    <p v-if="options.display_short_description" class="flex-text pt-2 my-3" v-html="post.description_changelog"></p>

                    <div class="d-flex justify-content-between align-items-end w-100">
                        <like-box v-if="options.enable_voting" :post="post" :display-number-votes="true" @click.stop></like-box>
                        <div class="social_share_buttons" v-if="options.display_social_sharing_buttons_changelog">
                            <b-button
                                variant="outline"
                                v-b-tooltip
                                title="Copy link"
                                class="copy-btn"
                                @click="copyLink(post.url)"
                            >
                                <i class="bi bi-clipboard"></i>
                            </b-button>
                            <b-button
                                variant="outline"
                                v-b-tooltip
                                title="Post on Facebook"
                                :href="`https://www.facebook.com/sharer/sharer.php?u=${post.url}`"
                                target="_blank"
                            >
                                <i class="bi bi-facebook"></i>
                            </b-button>
                            <b-button
                                variant="outline"
                                v-b-tooltip
                                title="Post on X"
                                :href="`https://x.com/intent/tweet?url=${post.url}&text=${encodeURIComponent(post.title)}`"
                                target="_blank"
                            >
                                <i class="bi bi-twitter-x"></i>
                            </b-button>
                            <b-button
                                variant="outline"
                                v-b-tooltip
                                title="Share on WhatsApp"
                                :href="`https://api.whatsapp.com/send?text=${post.url}`"
                                target="_blank"
                            >
                                <i class="bi bi-whatsapp"></i>
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import CategoryBadge from './CategoryBadge.vue';
import StatusBadge from './StatusBadge.vue';
import LikeBox from "./LikeBox.vue";
import NotificationPop
    from "../mixins/NotificationPop";

export default {
    components: {
        CategoryBadge,
        StatusBadge,
        LikeBox
    },
    mixins: [NotificationPop],
    data() {
        return {
            showDeleteConfirmModal: false,
            new_date: '',
            isChangelogDateModalVisible: false,
        }
    },
    props: {
        post: Object,
        options: Object,
        portalDisplayPostId: Boolean,
        isAdmin: {
            type: Boolean,
            required: true
        },
    },
    created() {
        this.new_date = this.post.latest_status_change_at
    },
    methods: {
        goToPost() {
            if (event.target.closest('.post-link')) {
                // If it was the a-href, do nothing and allow the default anchor behavior
                return;
            }
            console.log(this.post)
            window.location.href = this.post.url;
        },
        editPost() {
            window.location.href = this.post.url_edit
        },
        deletePost() {
            this.$http.delete(`/http_api/posts/${this.post.id}`)
                .then(() => {
                    this.showDeleteConfirmModal = false; // Close the modal after deletion
                    this.$emit('post-deleted', this.post.id);
                })
                .catch(error => {
                    console.error('Error deleting post:', error);
                    this.popNotification('Failed to delete the post. Please try again.', 'danger');
                });
        },

        copyLink(url) {
            navigator.clipboard.writeText(url).then(() => {
                this.popNotification('Link copied to clipboard', 'success');
            });
        },
        submitChangeDate() {
            this.$http({
                method: 'post',
                url: '/http_api/post.edit_changelog_date',
                params: {
                    key: this.post.id,
                    new_date: this.new_date,
                },
                headers: { 'Content-Type': 'application/json' }
            })
                .then(response => {
                    this.isChangelogDateModalVisible = false
                    this.post.latest_status_change_at_formatted = this.new_date
                    this.popNotification('Updated the changelog date. Reordering will be done on page refresh.', 'success');
                })
                .catch(error => {
                    console.error('Error :', error);
                    this.popNotification('Failed to change date.', 'danger');
                })
        },
    }
};
</script>

<style scoped>
.post-menu-dropdown {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1000;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.changelog-row:hover .post-menu-dropdown,
.changelog-row:hover .date-edit-button {
    opacity: 1;
}

.date-column {
    position: relative;
}

.date-edit-button {
    position: absolute;
    top: 0;
    right: 0;
    opacity: 0;
    transition: opacity 0.3s ease;
    padding: 0;
}

::v-deep .dropdown-menu {
    max-height: calc(100vh - 20px);
    overflow-y: auto;
}

::v-deep .card-menu-button {
    padding: 0.25rem 0.5rem;
    background: transparent;
    border: 1px solid #6c757d;
    border-radius: 0.25rem;
}

::v-deep .card-menu-button:hover {
    background-color: rgba(108, 117, 125, 0.1);
}

::v-deep .dropdown.show .card-menu-button {
    background-color: rgba(108, 117, 125, 0.1);
}

.changelog-row:hover .post-menu-dropdown {
    z-index: 1001;
}
</style>